// One date formatter for every machine-readable date the public site emits
// (schema.org datePublished/dateModified, OpenGraph article times, <time
// dateTime>, sitemap lastModified).
//
// Why this exists: `new Date('June 2, 2026').toISOString()` is NOT safe. That
// string parses as LOCAL midnight, so a server in UTC+2 serialises it as
// `2026-06-01T22:00:00.000Z` - the day before. In a client component the
// server and the browser can even disagree, which is a hydration mismatch on
// top of the wrong date. Nothing here goes through timezone math: the two
// shapes we actually ship ("YYYY-MM-DD" and "Month D, YYYY") are parsed by
// hand into a plain calendar day.

const MONTHS = [
	'january',
	'february',
	'march',
	'april',
	'may',
	'june',
	'july',
	'august',
	'september',
	'october',
	'november',
	'december',
];

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * A calendar day as `YYYY-MM-DD`, or `undefined` when the input is missing or
 * unparseable. Accepts:
 *   - an ISO string ("2026-06-02", "2026-06-02T10:00:00Z") - the day is taken
 *     verbatim, never re-derived through a local Date;
 *   - a human date ("June 2, 2026", "Jun 2 2026");
 *   - a `Date`, read with LOCAL getters (matches how the string forms above
 *     would have been parsed had they gone through `new Date`).
 *
 * `YYYY-MM-DD` is a valid ISO 8601 date, so it is accepted everywhere a full
 * timestamp would be (schema.org, OpenGraph, `<time dateTime>`). Keep full ISO
 * datetimes only where the source really is a timestamp.
 */
export function toIsoDay(input?: string | Date | null): string | undefined {
	if (!input) return undefined;

	if (input instanceof Date) {
		if (Number.isNaN(input.getTime())) return undefined;
		return `${input.getFullYear()}-${pad(input.getMonth() + 1)}-${pad(input.getDate())}`;
	}

	const value = input.trim();
	if (!value) return undefined;

	// Already ISO - take the day verbatim.
	const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s]|$)/.exec(value);
	if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

	// "Month D, YYYY" / "Mon D YYYY".
	const human = /^([A-Za-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/.exec(value);
	if (human) {
		const name = human[1].toLowerCase();
		const month = MONTHS.findIndex((m) => m === name || m.slice(0, 3) === name.slice(0, 3));
		if (month >= 0) return `${human[3]}-${pad(month + 1)}-${pad(Number(human[2]))}`;
	}

	// Anything else: fall back to Date parsing, then read it with LOCAL getters
	// so the day survives the round trip on the machine that parsed it.
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return undefined;
	return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
}

/**
 * The same calendar day as a `Date` pinned to UTC midnight - for consumers that
 * demand a Date object (Next's `MetadataRoute.Sitemap.lastModified`, which
 * serialises it with `toISOString()`). Pinning to UTC keeps the emitted day
 * equal to the day we parsed.
 */
export function toUtcDayDate(input?: string | Date | null): Date | undefined {
	const day = toIsoDay(input);
	return day ? new Date(`${day}T00:00:00.000Z`) : undefined;
}
